import {
  SimpleGrid,
  Box,
  Icon,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCode, FaDatabase, FaChartLine, FaMapMarkedAlt } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { Section } from '../layout/Section';

const MotionBox = motion(Box);

interface ServiceCardProps {
  icon: IconType;
  title: string;
  description: string;
  index: number;
}

const ServiceCard = ({ icon, title, description, index }: ServiceCardProps) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const iconColor = useColorModeValue('brand.500', 'brand.300');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <VStack
        p={8}
        bg={cardBg}
        borderRadius="xl"
        border="1px"
        borderColor={borderColor}
        spacing={4}
        h="full"
        _hover={{
          transform: 'translateY(-4px)',
          shadow: 'lg',
          borderColor: 'brand.500',
        }}
        transition="all 0.3s ease"
      >
        <Icon as={icon} w={12} h={12} color={iconColor} />
        <Text fontWeight="bold" fontSize="xl">
          {title}
        </Text>
        <Text color="gray.500" textAlign="center">
          {description}
        </Text>
      </VStack>
    </MotionBox>
  );
};

const services = [
  {
    icon: FaCode,
    title: 'Full-Stack Development',
    description:
      'Modern web applications built with React, TypeScript, and Node.js. From admin dashboards to customer-facing portals.',
  },
  {
    icon: FaDatabase,
    title: 'Data Engineering',
    description:
      'Scalable ETL pipelines, database architecture, and data warehousing solutions that handle millions of records.',
  },
  {
    icon: FaChartLine,
    title: 'Machine Learning',
    description:
      'Predictive models, analytics systems, and intelligent automation that turn your data into actionable insights.',
  },
  {
    icon: FaMapMarkedAlt,
    title: 'Visualization',
    description:
      'Interactive dashboards, geographic mapping, and data visualization that make complex information accessible.',
  },
];

export const Services = () => {
  const sectionBg = useColorModeValue('gray.50', 'gray.900');

  return (
    <Section
      id="services"
      title="What We Do"
      subtitle="End-to-end software solutions tailored to your needs"
      bg={sectionBg}
    >
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
        {services.map((service, index) => (
          <ServiceCard key={service.title} {...service} index={index} />
        ))}
      </SimpleGrid>
    </Section>
  );
};
